import axios from 'axios';

import {
    getCustomerSession,
    updateCustomerSession,
    clearCustomerSession,
    SESSION_EXPIRED_EVENT,
} from './customerSession';

const videocallshopApi = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Attach the customer identity token when logged in. The call-request
// capability token (Authorization: Bearer) keeps being sent explicitly by
// each action — both coexist.
videocallshopApi.interceptors.request.use((config) => {
    const session = getCustomerSession();
    if (session && session.accessToken && !config.headers['x-customer-token']) {
        config.headers['x-customer-token'] = `Bearer ${session.accessToken}`;
    }
    return config;
});

// On 401: rotate the refresh token ONCE, retry the original request; if the
// refresh itself fails the session is dead -> clear it and notify the app.
let refreshPromise = null;

const refreshSession = async () => {
    const session = getCustomerSession();
    if (!session || !session.refreshToken) {
        throw new Error('No refresh token.');
    }

    // plain axios: must not recurse into these interceptors
    const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/customer/refresh`,
        { refreshToken: session.refreshToken }
    );

    updateCustomerSession({
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
    });

    return data.data.accessToken;
};

videocallshopApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        const session = getCustomerSession();

        const isCustomerAuthRoute =
            config && config.url && config.url.includes('/auth/customer/');

        if (
            !response ||
            response.status !== 401 ||
            !session ||
            !session.refreshToken ||
            config._customerRetried ||
            isCustomerAuthRoute
        ) {
            return Promise.reject(error);
        }

        try {
            if (!refreshPromise) {
                refreshPromise = refreshSession().finally(() => {
                    refreshPromise = null;
                });
            }
            const accessToken = await refreshPromise;

            config._customerRetried = true;
            config.headers['x-customer-token'] = `Bearer ${accessToken}`;
            return videocallshopApi(config);
        } catch (refreshErr) {
            clearCustomerSession();
            window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
            return Promise.reject(error);
        }
    }
);

export default videocallshopApi;
